import { getCareSourceAttribution } from './careSourceAttribution';
import { readMedicationDose } from "../../shared/medicationDose";
const normalizeWhitespace = (value = "") =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim();
export const buildMedicationFromSuggestion = (suggestion = {}, source = {}) => {
  const canonicalRecord =
    suggestion.canonicalMedicationRecord &&
    typeof suggestion.canonicalMedicationRecord === "object"
      ? suggestion.canonicalMedicationRecord
      : null;
  const canonicalSchedule = canonicalRecord?.schedule || {};
  const quantity = readMedicationDose({
    dose: canonicalRecord?.medication?.dose ?? suggestion.dose,
    unit: canonicalRecord?.medication?.unit ?? suggestion.unit,
    doseText: canonicalRecord?.medication?.doseText || suggestion.doseText,
    medicationName: suggestion.name,
    sourceText: suggestion.sourceText || source.rawText,
  });
  if ((suggestion.dose || suggestion.doseText) && quantity.needsClarification)
    throw new Error(quantity.reason);
  const dose = quantity.amount,
    unit = quantity.unit,
    doseText = quantity.doseText;
  const notes = normalizeWhitespace(
    suggestion.notes || suggestion.purpose || "",
  );
  const needsReview = Boolean(suggestion.needsReview) || !dose;
  const reviewReasons = Array.isArray(suggestion.reviewReasons)
    ? suggestion.reviewReasons
    : [];
  const resolvedReviewReasons =
    !dose && !reviewReasons.includes("missing_dose")
      ? [...reviewReasons, "missing_dose"]
      : reviewReasons;
  const times =
    Array.isArray(suggestion.times) && suggestion.times.length
      ? suggestion.times
      : [suggestion.time].filter(Boolean);
  const scheduleType =
    canonicalSchedule.type === "interval"
      ? "every_x_hours"
      : canonicalSchedule.type === "as_needed" ||
          /^(?:as|if) needed$/i.test(
            normalizeWhitespace(suggestion.status || ""),
          )
        ? "as_needed"
        : "scheduled";
  const schedules = times.length
    ? times.map((time, index) => ({
        id: `${suggestion.id}-${index}`,
        dose,
        unit,
        doseText,
        time,
      }))
    : [
        {
          id: `${suggestion.id}-0`,
          dose,
          unit,
          doseText,
          time: "",
        },
      ];

  return {
    id: suggestion.id,
    name: suggestion.name || "",
    dose,
    unit,
    doseText,
    time: times[0] || "",
    form: suggestion.form || "pill",
    genericName: suggestion.genericName || "",
    brandName: suggestion.brandName || "",
    aliases: Array.isArray(suggestion.aliases) ? suggestion.aliases : [],
    category: /vitamin|supplement/i.test(
      `${suggestion.name} ${suggestion.purpose} ${notes}`,
    )
      ? "supplement"
      : "prescription",
    scheduleType,
    intervalHours: canonicalSchedule.intervalHours || null,
    durationDays: canonicalSchedule.durationDays || null,
    purpose: suggestion.purpose || "",
    notes,
    prescribingDoctor: suggestion.prescribingDoctor || "",
    sourceScheduleText: suggestion.schedule || "",
    sourceStatus: suggestion.status || "",
    needsReview,
    reviewReasons: resolvedReviewReasons,
    planHistory: Array.isArray(suggestion.history) ? suggestion.history : [],
    sourceDocuments: [
      {
        sourceId: source.id || "",
        ...getCareSourceAttribution(source),
        title:
          source.sourceMetadata?.fileName ||
          source.metadata?.source?.fileName ||
          source.fileName ||
          "Medication source",
        addedAt: new Date().toISOString(),
      },
    ],
    schedules,
    temporaryHold:
      suggestion.temporaryHold && typeof suggestion.temporaryHold === "object"
        ? { ...suggestion.temporaryHold }
        : null,
    completedCourse:
      suggestion.completedCourse &&
      typeof suggestion.completedCourse === "object"
        ? { ...suggestion.completedCourse }
        : null,
    canonicalMedicationRecord: {
      ...(canonicalRecord || {}),
      schemaVersion: 1,
      recordKind: canonicalRecord?.recordKind || "plan",
      action: canonicalRecord?.action || "record_current",
      medication: {
        ...(canonicalRecord?.medication || {}),
        dose,
        unit,
        doseText,
      },
      schedule: { ...(canonicalRecord?.schedule || {}) },
      effective: { ...(canonicalRecord?.effective || {}) },
      ...(canonicalRecord?.actionDetails
        ? { actionDetails: { ...canonicalRecord?.actionDetails } }
        : {}),
      source: { ...(canonicalRecord?.source || {}) },
    },
  };
};
